import axios from 'axios';
import languages from 'static/languages.json';
import countries from 'static/countries.json';
import config from '../config';

import {
  getProfile,
  login,
  logout,
  addNotificationToken,
  createPassword,
  changePassword,
  updateUserProfile,
} from './auth';
import {
  fetchUserList,
  fetchUserDetail,
  addUser,
  updateUser,
  updatePasscode,
  fetchPersonnelTemplateCSV,
  uploadPersonnelCSV,
} from './users';
import {
  fetchEventListPdf,
  fetchEventList,
  fetchEventDetailsPdf,
  fetchEventDetails,
  addEvent,
  updateEvent,
  uploadMedia,
  updateEventDistribution,
  assignEventToPersonnel,
  sendEventForReview,
  approveEvent,
  rejectEvent,
  setInappropriate,
  createEventType,
  updateEventType,
  fetchEventTypes,
} from './events';
import {
  fetchPatrolList,
  fetchPatrolReportDetail,
  fetchPatrolListPdf,
  fetchPatrolDetailPdf,
  assignPatrolToPersonnel,
  startVirtualPatrol,
  uploadVirtualPatrol,
  abortVirtualPatrol,
  getPatrolById,
  postEvidenceTask,
} from './patrol';
import {
  fetchPatrolDefList,
  fetchPatrolDefDetails,
  addPatrolDef,
  updatePatrolDef,
  getPatrolQr,
  getLocationImage,
  fetchPatrolDashboard,
} from './patrolDef';
import {
  fetchDistresses,
  fetchDistressDetails,
  assignResponder,
  abortDistress,
  fetchDistressListPdf,
  fetchDistressDetailsPdf,
} from './distress';
import {
  fetchTripList,
  fetchTripListPdf,
  fetchTripDetail,
  fetchTripDetailsPdf,
  abortTrip,
  getPriceRangeForServiceType,
  createTransportServiceType,
  editTransportServiceType,
  fetchTripTypeDetail,
  assignDriver,
} from './trips';

import {
  getAssetList,
  fetchAssetDetail,
  fetchAssetByCategory,
  addAsset,
  updateAsset,
  generateOtp,
  assetHandover,
  makeStatusAvailable,
  makeStatusUnavailable,
  requestAssetReturn,
  updateAssetPasscode,
} from './asset';

import {
  getAssetTypeList,
  createAssetType,
  updateAssetType,
} from './assetType';

import {
  fetchBusinessClientList,
  fetchBusinessClientDetail,
  addBusinessClient,
  updateBusinessClient,
} from './businessClient';

import {
  fetchSecurityCompanies,
  fetchSecurityCompanyById,
  fetchSecurityCompanyServicesById,
  fetchPendingSecurityCompanies,
  fetchPendingCompanyDetail,
  rejectPendingCompany,
  approvePendingCompany,
  fetchSecurityCompanyBillingInfo,
  fetchSecurityCompanyBillingSetupInfo,
  fetchAllAvailablePlans,
  changeSubscription,
  updateSecurityCompany,
  cancelSubscription,
  costEstimate,
  createPendingCompany,
} from './securityCompany';

import {
  fetchRegionList,
  fetchRegionDetails,
  fetchZoneList,
  fetchZoneDetails,
  fetchAddressList,
  createZone,
  updateZone,
  createRegion,
  updateRegion,
} from './regionAndZone';

import {
  fetchSitesList,
  fetchSiteDetails,
  createSite,
  updateSite,
} from './site';

import {
  getVisitList,
  logoutVisitor,
  getVisitDetail,
  fetchVisitListPdf,
  fetchVisitDetailsPdf,
  fetchVisitorTypes,
  createVisitorTypes,
  updateVisitorTypes,
  getPreviousVisitorList,
  getPurposeList,
} from './visitor';

import {
  fetchTaskList,
  fetchTaskDetails,
  taskUpload,
  getTaskQr,
  fetchCleanerTaskDetailsPdf,
  fetchCleanerTaskListPdf,
  fetchCancellableTaskList,
  putTaskSchedule,
  fetchTaskSchedule,
  fetchTaskTemplates,
  fetchTaskTypes,
  updateTaskType,
} from './task';

import {
  fetchAttendanceList,
  fetchShiftById,
  fetchAttendanceTrackingListCSV,
  fetchAttendanceTrackingListPDF,
  createShift,
  updateShift,
  deleteShift,
} from './attendanceTracking';

import {
  fetchDepartmentList,
  fetchDepartmentById,
  updateOrCreateDepartment,
  fetchGlobalDepartments,
} from './departments';

import { fetchMaintainerTaskListPdf } from './maintenance';

import { getTripTypeList, createTripType, updateTripType } from './tripType';

import { getMediaUrl, uploadMediaItem } from './media';

import {
  fetchMessagingList,
  sendMessage,
  acknowledgeMessage,
} from './messaging';

import {
  fetchGlobalUsers,
  fetchGlobalSites,
  fetchGlobalBusinessClients,
} from './globalList';

import {
  fetchAssetDashboardItems,
  fetchPatrolDashboardItems,
  fetchPersonnelDashboardItems,
  fetchTaskDashboardItems,
  fetchTripDashboardItems,
  fetchVisitDashboardItems,
} from './dashboard';

const serviceUrl = config.api;

const API = {
  // public apis
  verifyToken: (userId, token) =>
    axios.get(`${serviceUrl}/users/valid-token?u=${userId}&t=${token}`),
  resetPassword: user =>
    axios.patch(`${serviceUrl}/users/reset-password`, { data: user }),
  recoverPassword: user =>
    axios.post(`${serviceUrl}/users/forgot-password`, { data: user }),
  emailVerify: (userId, token) =>
    axios.patch(`${serviceUrl}/users/verify-email`, {
      data: { token: token, userId: userId },
    }),
  // auth related apis
  getProfile,
  updateUserProfile,
  login,
  logout,
  addNotificationToken,
  createPassword,
  changePassword,
  // user related apis
  fetchUserList,
  fetchUserDetail,
  addUser,
  updateUser,
  updatePasscode,
  fetchPersonnelTemplateCSV,
  uploadPersonnelCSV,
  // businessClient related apis
  fetchBusinessClientList,
  fetchBusinessClientDetail,
  addBusinessClient,
  updateBusinessClient,
  //securityCompany related apis
  fetchSecurityCompanies,
  fetchSecurityCompanyById,
  fetchSecurityCompanyServicesById,
  fetchPendingSecurityCompanies,
  fetchPendingCompanyDetail,
  rejectPendingCompany,
  approvePendingCompany,
  fetchSecurityCompanyBillingInfo,
  fetchSecurityCompanyBillingSetupInfo,
  fetchAllAvailablePlans,
  changeSubscription,
  updateSecurityCompany,
  cancelSubscription,
  costEstimate,
  // distress related apis
  fetchDistresses,
  fetchDistressDetails,
  assignResponder,
  abortDistress,
  fetchDistressListPdf,
  fetchDistressDetailsPdf,
  // region and zone related apis
  fetchRegionList, //fetch region list
  fetchRegionDetails, // fetch regions
  createRegion,
  updateRegion,
  createPendingCompany,
  fetchZoneList, // fetch zones
  fetchAddressList, // fetch address list
  fetchZoneDetails,
  createZone,
  updateZone,
  // site related apis
  fetchSitesList,
  fetchSiteDetails,
  createSite,
  updateSite,
  // patrolDefinition related apis
  fetchPatrolDefList, //fetch patrol Def List
  fetchPatrolDefDetails,
  addPatrolDef,
  updatePatrolDef,
  getPatrolQr,
  // patrol related apis
  fetchPatrolList,
  fetchPatrolReportDetail, //fetch Patrol report details
  fetchPatrolListPdf,
  fetchPatrolDetailPdf,
  assignPatrolToPersonnel,
  getLocationImage,
  fetchPatrolDashboard,
  startVirtualPatrol,
  uploadVirtualPatrol,
  abortVirtualPatrol,
  getPatrolById,
  postEvidenceTask,
  // events related apis
  fetchEventList,
  fetchEventListPdf,
  fetchEventDetails,
  fetchEventDetailsPdf,
  addEvent,
  updateEvent,
  uploadMedia,
  updateEventDistribution, //update event distribution
  assignEventToPersonnel, //assign an event to a Responder/Supervisor
  sendEventForReview, //Send an event for review
  approveEvent, //Approve an event
  rejectEvent, //Reject an event
  setInappropriate, //set an event to Inappropriate
  //event types
  createEventType,
  updateEventType,
  fetchEventTypes,
  // asset related apis
  getAssetList, //fetch asset
  fetchAssetDetail, //fetch asset detail
  fetchAssetByCategory,
  addAsset, //add new asset
  updateAsset,
  generateOtp,
  assetHandover, //make asset available
  makeStatusAvailable, //make asset available
  makeStatusUnavailable, //make asset Unavailable
  requestAssetReturn,
  updateAssetPasscode,
  // assetType related apis
  getAssetTypeList, //fetch asset type list
  createAssetType, // add asset type
  updateAssetType,
  // assetType related apis
  getTripTypeList, //fetch asset type list
  createTripType, // add asset type
  updateTripType,
  // trips related apis
  fetchTripList,
  fetchTripDetail, //fetch trip detail
  fetchTripTypeDetail, //fetch trip detail
  fetchTripDetailsPdf,
  assignDriver,
  abortTrip, //abort trip
  getPriceRangeForServiceType,
  createTransportServiceType,
  editTransportServiceType,
  fetchTripListPdf,
  // visitor related api
  getVisitList,
  getVisitDetail,
  fetchVisitListPdf,
  fetchVisitDetailsPdf,
  getPreviousVisitorList,
  logoutVisitor,
  fetchVisitorTypes,
  createVisitorTypes,
  updateVisitorTypes,
  getPurposeList,
  // tasks
  fetchTaskList,
  fetchTaskDetails,
  taskUpload,
  getTaskQr,
  putTaskSchedule,
  fetchTaskSchedule,
  fetchCleanerTaskDetailsPdf,
  fetchCleanerTaskListPdf,
  fetchCancellableTaskList,
  fetchTaskTemplates,
  //task types
  fetchTaskTypes,
  updateTaskType,
  //maintenance
  fetchMaintainerTaskListPdf,
  //media
  getMediaUrl,
  uploadMediaItem,
  // messaging
  fetchMessagingList,
  sendMessage,
  acknowledgeMessage,
  // global list
  fetchGlobalUsers,
  fetchGlobalSites,
  fetchGlobalBusinessClients,
  //attendance Tracking
  fetchAttendanceList,
  fetchShiftById,
  fetchAttendanceTrackingListCSV,
  fetchAttendanceTrackingListPDF,
  createShift,
  updateShift,
  deleteShift,
  // department related apis
  fetchDepartmentList,
  fetchDepartmentById,
  updateOrCreateDepartment,
  fetchGlobalDepartments,
  //dashboard
  fetchTaskDashboardItems,
  fetchPatrolDashboardItems,
  fetchTripDashboardItems,
  fetchPersonnelDashboardItems,
  fetchAssetDashboardItems,
  fetchVisitDashboardItems,
  // fetch miscellaneous data {data: {currentPassword: oldPwd, newPassword: newPwd}
  getCountryList: () =>
    Promise.resolve({
      data: {
        data: countries,
      },
    }),
  getLanguageList: () =>
    Promise.resolve({
      data: {
        data: languages,
      },
    }),
};

export default API;
